import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, Lock, DollarSign, TrendingUp, ArrowRight,
  AlertCircle, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletBreakdown = ({ compact = false }) => {
  const navigate = useNavigate();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const res = await axios.get(`${API}/wallet/summary`, { withCredentials: true });
      setWalletData(res.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-white/50" />
      </div>
    );
  }

  if (!walletData) {
    return null;
  }

  // Calculate values
  const creditRate = 100; // $1 = 100 credits
  const availableBalance = (walletData.credits_balance || 0) / creditRate;
  const lockedBalance = walletData.locked_balance || 0;
  const winnings = walletData.cash_balance || 0;
  const totalBalance = availableBalance + lockedBalance + winnings;
  const activeMatches = walletData.active_matches || 0;

  // Compact version for sidebar/header
  if (compact) {
    return (
      <div 
        className="glass-card rounded-lg p-3 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => navigate('/wallet')}
        data-testid="wallet-breakdown-compact"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/50 text-xs">Total Balance</span>
          <RefreshCw 
            className="w-3 h-3 text-white/30 hover:text-white/60 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); fetchWalletData(); }}
          />
        </div>
        <p className="font-mono text-xl font-bold text-white">${totalBalance.toFixed(2)}</p>
        
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-[#00F0FF]">
              <DollarSign className="w-3 h-3" /> Available
            </span>
            <span className="font-mono">${availableBalance.toFixed(2)}</span>
          </div>
          {lockedBalance > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-[#FFB800]">
                <Lock className="w-3 h-3" /> In Matches
              </span>
              <span className="font-mono">${lockedBalance.toFixed(2)}</span>
            </div>
          )}
          {winnings > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-[#00FF94]">
                <TrendingUp className="w-3 h-3" /> Winnings
              </span>
              <span className="font-mono">${winnings.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full version for wallet page
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl p-6"
      data-testid="wallet-breakdown-full"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7000FF] to-[#00F0FF] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Balance Overview</h3>
            <p className="text-white/50 text-sm">Your complete wallet breakdown</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchWalletData}
          className="text-white/50 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Total Balance */}
      <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-[#7000FF]/20 to-[#00F0FF]/20 border border-[#7000FF]/30">
        <p className="text-white/50 text-sm mb-1">Total Balance</p>
        <p 
          className="font-mono text-4xl font-bold"
          style={{ 
            background: 'linear-gradient(90deg, #00F0FF, #00FF94)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          ${totalBalance.toFixed(2)}
        </p>
      </div>

      {/* Balance Breakdown */}
      <div className="space-y-3">
        {/* Available Balance */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <p className="font-medium text-[#00F0FF]">Available Balance</p>
              <p className="text-white/40 text-xs">Ready for match entry</p>
            </div>
          </div>
          <p className="font-mono text-xl font-bold text-[#00F0FF]">${availableBalance.toFixed(2)}</p>
        </div>

        {/* Locked in Matches */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFB800]/20 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#FFB800]" />
            </div>
            <div>
              <p className="font-medium text-[#FFB800]">Locked in Matches</p>
              <p className="text-white/40 text-xs">
                {activeMatches > 0 
                  ? `${activeMatches} active match${activeMatches > 1 ? 'es' : ''}`
                  : 'No active matches'
                }
              </p>
            </div>
          </div>
          <p className="font-mono text-xl font-bold text-[#FFB800]">${lockedBalance.toFixed(2)}</p>
        </div>

        {/* Winnings (Withdrawable) */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00FF94]/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#00FF94]" />
            </div>
            <div>
              <p className="font-medium text-[#00FF94]">Winnings</p>
              <p className="text-white/40 text-xs">Available for withdrawal</p>
            </div>
          </div>
          <p className="font-mono text-xl font-bold text-[#00FF94]">${winnings.toFixed(2)}</p>
        </div>
      </div>

      {/* Visual Bar */}
      <div className="mt-6">
        <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
          {availableBalance > 0 && (
            <div 
              className="bg-[#00F0FF] transition-all duration-500"
              style={{ width: `${(availableBalance / totalBalance) * 100}%` }}
            />
          )}
          {lockedBalance > 0 && (
            <div 
              className="bg-[#FFB800] transition-all duration-500"
              style={{ width: `${(lockedBalance / totalBalance) * 100}%` }}
            />
          )}
          {winnings > 0 && (
            <div 
              className="bg-[#00FF94] transition-all duration-500"
              style={{ width: `${(winnings / totalBalance) * 100}%` }}
            />
          )}
        </div>
        <div className="flex justify-between mt-2 text-xs text-white/40">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#00F0FF]" /> Available
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#FFB800]" /> Locked
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#00FF94]" /> Winnings
          </span>
        </div>
      </div>

      {/* Info Note */}
      {lockedBalance > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-white/5 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-white/50">
            Locked funds are your entry fees held in escrow for active matches. 
            They will be released when matches complete.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default WalletBreakdown;
